/* eslint-disable @typescript-eslint/no-explicit-any */
import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import localforage, { setItem } from 'localforage';
import { StationDetails, MobileConfig, Lane } from '../api/api-service';

// Configure localforage
if (typeof window !== 'undefined') {
  localforage.config({
    name: 'koiguard-mobile',
    storeName: 'app_state',
  });
}

// Create a custom storage for Jotai using localforage
const storage = {
  getItem: async (key: string, initialValue: any) => {
    const value = await localforage.getItem(key);
    return value !== null ? value : initialValue;
  },

  setItem: async (key: string, value: any) => {
    await localforage.setItem(key, value);
  },

  removeItem: async (key: string) => {
    await localforage.removeItem(key);
  },
};

// Global clear utility for the custom storage
export const clearAppStorage = async (excludedKeys: string[] = ['pre_check_trailer_ids']) => {
  const backups = await Promise.all(
    excludedKeys.map(async (key) => {
      const value = await localforage.getItem(key);
      return { key, value };
    }),
  );

  await localforage.clear();

  if (typeof window !== 'undefined') {
    sessionStorage.clear();
  }

  await Promise.all(
    backups.map(async ({ key, value }) => {
      if (value !== null) {
        await localforage.setItem(key, value as string);
      }
    }),
  );
};

export interface CheckinFormData {
  mode: string;
  laneID: string;
  badgeId: string;
  vehicleType: string;
  deliveryType: string;
  trailerNumber: string;
  scac: string;
  lpn: string;
  driverName: string;
  driverLicense: string;
  supplier: string;
  loadStatus: string;
  hazmat: string;
  suppliers: string[];
  images: string[]; // Base64 encoded images
  assetID: string;
  isBadgeVerified: boolean | null;
  appointmentID?: string;
  appointmentTime?: string;
  dropZone?: string;
  createNewAppointment?: boolean;
  raiseAppointmentDisputeToLogistics?: boolean;
  driverLicenseState: string;
  loadTypeCode: string;
  isLiveLoad: string;
  tractorNumber: string;
}

export interface CheckinResponse {
  token?: string;
  message?: string;
  dropzone?: string;
  dropspot?: string;
  ymsResult?: ymsResultType;
}

export interface ymsResultType {
  appointment_id: number;
  dropZone: string;
  message: string;
  success: boolean;
  dropSpot: string;
}
export interface VerificationDataType {
  status: string;
  token: string;
  timestamp: string;
}

export interface AssignedLaneDataType {
  laneID: string;
  stationID: string;
}

const initialFormData: CheckinFormData = {
  mode: '',
  laneID: '',
  badgeId: '',
  vehicleType: '',
  deliveryType: '',
  trailerNumber: '',
  scac: '',
  lpn: '',
  driverName: '',
  driverLicense: '',
  supplier: '',
  loadStatus: '',
  hazmat: '',
  suppliers: [],
  images: [],
  assetID: '',
  isBadgeVerified: null,
  driverLicenseState: '',
  loadTypeCode: '',
  isLiveLoad: '',
  tractorNumber: '',
};

interface TimerState {
  endTime: number;
  originalWaitTime: number;
}

const initialCheckinResponse: CheckinResponse = {};

// Persisted atoms
export const formDataAtom = atomWithStorage<CheckinFormData>('form_data', initialFormData, storage);
export const checkinResponseAtom = atomWithStorage<CheckinResponse>(
  'checkin_response',
  initialCheckinResponse,
  storage,
);
export const stationDetailsAtom = atomWithStorage<StationDetails | null>(
  'station_details',
  null,
  storage,
);
export const mobileConfigAtom = atomWithStorage<MobileConfig | null>(
  'mobile_config',
  null,
  storage,
);
export const socketLaneDataAtom = atom<Lane[] | null>(null);

// UI State atoms
export const currentScreenAtom = atomWithStorage<string>('current_screen', 'SPLASH', storage);
export const navigationHistoryAtom = atomWithStorage<string[]>('navigation_history', [], storage);
export const isLoadingAtom = atom<boolean>(false);
export const validationTriggerAtom = atom<number>(0);
export const selectedLanguageAtom = atomWithStorage<string>('selected_language', 'en', storage);

// Connectivity atoms
export const isOnlineAtom = atom<boolean>(typeof window !== 'undefined' ? navigator.onLine : true);

// Tab blocking state
export const isTabBlockedAtom = atom<boolean>(false);

// Conflict state
export const isConflictRaisedAtom = atomWithStorage<boolean>('is_conflict_raised', false, storage);

// Socket data atoms
export const socketCheckInVerificationDataAtom = atomWithStorage<VerificationDataType | null>(
  'socket_checkin_verification',
  null,
  storage,
);
export const socketAssignedLaneDataAtom = atomWithStorage<AssignedLaneDataType | null>(
  'socket_assigned_lane',
  null,
  storage,
);
export const socketDisputeResolvedDataAtom = atomWithStorage<VerificationDataType | null>(
  'socket_dispute_resolved',
  null,
  storage,
);

export const socketLaneConflictVoided = atomWithStorage<boolean>(
  'socket_lane_conflict_void',
  false,
  storage,
);

export const waitingScreenTextAtom = atomWithStorage<{
  title: string;
  description: string;
  waitTime?: number;
}>(
  'waiting_screen_text',
  {
    title: '',
    description: '',
    waitTime: 0,
  },
  storage,
);

export const sessionExpTimeAtom = atomWithStorage<string>('session_expiry_time', '', storage);

export const timerStateAtom = atomWithStorage(
  'timerState',
  null as { endTime: number; originalWaitTime: number } | null,
  storage,
);

export const tokenSessionIDs = atomWithStorage<string[]>('token_session_ids', [], storage);

export const currentTrailerId = atomWithStorage<string[]>('pre_check_trailer_ids', [], storage);

export const isResolvedConflictAtom = atomWithStorage<boolean>(
  'is_resolved_conflict',
  false,
  storage,
);

export const reservedLaneIDAtom = atomWithStorage<string>('reserved_lane_id', '', storage);
